import React, { useState } from "react";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"
).replace(/\/$/, "");

export default function AadhaarVerificationManager() {
  const [userId, setUserId] = useState("");
  const [expiresMinutes, setExpiresMinutes] = useState("2520");
  const [verificationUrl, setVerificationUrl] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [messageCopied, setMessageCopied] = useState(false);

  const notify = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
  };

  const validate = () => {
    if (!/^\d+$/.test(userId.trim()) || Number(userId) <= 0) {
      notify("Enter a valid positive user ID.", "error");
      return false;
    }
    return true;
  };

  const getLoggedInUserId = () => {
    const directId =
      localStorage.getItem("user_id") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id");
    if (directId && /^\d+$/.test(directId)) return directId;

    for (const key of ["user", "userData", "currentUser", "authUser"]) {
      try {
        const value = JSON.parse(localStorage.getItem(key) || "null");
        const id = value?.id ?? value?.user?.id;
        if (id !== undefined && id !== null && /^\d+$/.test(String(id))) {
          return String(id);
        }
      } catch {
        // Try the next known storage key.
      }
    }
    return "";
  };

  const requestJson = async (url, options = {}) => {
    const token = localStorage.getItem("jwt") || localStorage.getItem("token");
    const adminId = getLoggedInUserId();
    if (!adminId) {
      throw new Error("Could not find the logged-in administrator's user ID.");
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-User-Id": String(adminId),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data.detail || data.message || `Request failed (${response.status}).`
      );
    }
    return data;
  };

  const createLink = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading("create");
    notify("");
    setVerificationUrl("");
    setRecipient(null);

    try {
      const data = await requestJson(
        `${API_BASE_URL}/api/aadhaar-verification/${encodeURIComponent(userId.trim())}/create-link`,
        {
          method: "POST",
          body: JSON.stringify({ expires_minutes: Number(expiresMinutes) || 2520 }),
        }
      );

      if (!data.verification_url) {
        throw new Error("The API did not return verification_url.");
      }

      setVerificationUrl(data.verification_url);
      setRecipient({
        name: data.recipient_name || "",
        phone: data.recipient_phone || "",
        userId: data.user_id ?? userId.trim(),
        expiresMinutes: data.expires_in_minutes ?? (Number(expiresMinutes) || 2520),
      });
      setStatusData(null);

      if (!data.recipient_phone) {
        notify(
          "Link created, but recipient_phone was not returned. Check your backend create-link response.",
          "error"
        );
      } else {
        notify("Verification link created. Review the recipient before sending.", "success");
      }
    } catch (error) {
      notify(error.message || "Could not create the verification link.", "error");
    } finally {
      setLoading("");
    }
  };

  const copyLink = async () => {
    if (!verificationUrl) return;
    try {
      await navigator.clipboard.writeText(verificationUrl);
      notify("Verification link copied to clipboard.", "success");
    } catch {
      notify("Copy failed. Select the link and copy it manually.", "error");
    }
  };

  const copyPreviewMessage = async () => {
  const previewMessage = `Hello ${recipient?.name || "Customer"},

As part of the Snap4Me account verification process,
please complete your Aadhaar verification using the
secure link below:

${verificationUrl}

Please complete the verification to proceed with
your account verification process.

Thank you,
Team Snap4Me`;

  try {
    await navigator.clipboard.writeText(previewMessage);

    setMessageCopied(true);
    notify("Complete WhatsApp message copied successfully.", "success");

    setTimeout(() => {
      setMessageCopied(false);
    }, 2000);

  } catch {
    notify("Could not copy the message. Please try again.", "error");
  }
};
  const sendWhatsAppLink = async () => {
    if (!validate()) return;
    if (!verificationUrl) {
      notify("Generate a verification link first.", "error");
      return;
    }
    if (!recipient?.phone) {
      notify("Recipient phone number is unavailable. Check the create-link API response.", "error");
      return;
    }

    setLoading("whatsapp");
    notify("");
    try {
      const data = await requestJson(
        `${API_BASE_URL}/api/aadhaar-verification/${encodeURIComponent(userId.trim())}/send-link`,
        {
          method: "POST",
          body: JSON.stringify({ verification_url: verificationUrl }),
        }
      );
      notify(data.message || "WhatsApp message submitted to Twilio.", "success");
    } catch (error) {
      notify(error.message || "Could not send the WhatsApp verification link.", "error");
    } finally {
      setLoading("");
    }
  };

  const checkStatus = async () => {
    if (!validate()) return;
    setLoading("status");
    notify("");
    try {
      const data = await requestJson(
        `${API_BASE_URL}/api/aadhaar-verification/${encodeURIComponent(userId.trim())}/status`,
        { method: "GET" }
      );
      setStatusData(data.verification || null);
      if (!data.verification) {
        notify("No Aadhaar verification session was found for this user.", "info");
      } else if (data.verification.status === "verified") {
        notify("Aadhaar verification is complete for this user.", "success");
      } else {
        notify(`Current verification status: ${data.verification.status || "unknown"}.`, "info");
      }
    } catch (error) {
      notify(error.message || "Could not retrieve verification status.", "error");
      setStatusData(null);
    } finally {
      setLoading("");
    }
  };

  const statusLabel = statusData?.status || "No status";
  const statusClass =
    statusLabel === "verified"
      ? "avm-status-verified"
      : statusLabel === "failed"
      ? "avm-status-failed"
      : "avm-status-pending";

  return (
    <div className="avm-page">
      <style>{`
        .avm-page{padding:12px 24px;max-width:1240px;margin:0 auto;color:#172033;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
        .avm-header{margin-bottom:12px}.avm-eyebrow{display:none}
        .avm-header h1{font-size:25px;letter-spacing:-.5px;margin:0 0 4px;font-weight:760;line-height:1.2}.avm-header p{margin:0;color:#64748b;line-height:1.45;max-width:760px;font-size:13px}
        .avm-card{background:#fff;border:1px solid #e2e8f0;border-radius:16px;padding:18px;margin-bottom:14px;box-shadow:0 7px 24px rgba(15,23,42,.045)}
        .avm-card h2{font-size:18px;margin:0 0 8px}.avm-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px}
        .avm-field label{display:block;font-size:13px;font-weight:700;margin-bottom:8px;color:#334155}
        .avm-field input,.avm-field select{width:100%;box-sizing:border-box;padding:12px 13px;border:1px solid #cbd5e1;border-radius:10px;background:#fff;font-size:14px;color:#0f172a;outline:none}
        .avm-field input:focus,.avm-field select:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.12)}
        .avm-actions{display:flex;flex-wrap:wrap;gap:10px;margin-top:14px}.avm-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border:0;border-radius:10px;padding:12px 16px;font-size:13px;font-weight:700;cursor:pointer;min-height:42px;transition:background .15s,transform .15s}
        .avm-btn:hover:not(:disabled){transform:translateY(-1px)}.avm-btn:disabled{opacity:.55;cursor:not-allowed}
        .avm-primary{background:#245eea;color:#fff}.avm-primary:hover:not(:disabled){background:#1d4ed8}.avm-secondary{background:#eef2f7;color:#263449}.avm-secondary:hover:not(:disabled){background:#e2e8f0}
        .avm-message{padding:8px 12px;border-radius:10px;margin:0 0 12px;font-size:12px;line-height:1.35;overflow-wrap:anywhere;border:1px solid transparent}
        .avm-message-info{background:#eff6ff;color:#1d4ed8;border-color:#dbeafe}.avm-message-success{background:#ecfdf5;color:#065f46;border-color:#bbf7d0}.avm-message-error{background:#fff1f2;color:#9f1239;border-color:#fecdd3}
        .avm-note{font-size:11px;color:#64748b;line-height:1.45;margin-top:10px}.avm-share-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:16px;margin-top:16px;align-items:stretch}
        .avm-share-panel{min-width:0;padding:17px;border:1px solid #e2e8f0;border-radius:14px;background:#fff}.avm-wa-panel{background:linear-gradient(180deg,#fbfefc,#f5faf7);border-color:#d7eadf}
        .avm-panel-heading{display:flex;align-items:flex-start;gap:10px;margin-bottom:12px}.avm-panel-icon{width:40px;height:40px;flex:0 0 40px;display:flex;align-items:center;justify-content:center;border-radius:12px;background:#eef4ff;color:#245eea;font-size:20px}
        .avm-panel-icon-green{background:#e8f7ee;color:#16845b}.avm-panel-heading h3{margin:1px 0 4px;font-size:15px}.avm-panel-heading p{margin:0;color:#64748b;font-size:12px;line-height:1.5}
        .avm-meta-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 0;border-bottom:1px solid #edf1f5;font-size:13px}.avm-meta-row span{color:#64748b}.avm-meta-row strong{text-align:right;overflow-wrap:anywhere}
        .avm-url-label{display:block;font-size:12px;font-weight:700;color:#475569;margin:12px 0 6px}.avm-url-value{display:block;width:100%;box-sizing:border-box;font-size:12px;line-height:1.55;color:#334155;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px;overflow-wrap:anywhere;word-break:break-word}
        .avm-recipient{display:flex;align-items:center;gap:10px;background:#fff;border:1px solid #e0eee5;border-radius:11px;padding:9px;margin-bottom:10px}.avm-recipient-avatar{width:40px;height:40px;border-radius:50%;background:#e4f5e9;color:#16794b;display:flex;align-items:center;justify-content:center;font-size:17px;font-weight:800;flex-shrink:0}
        .avm-recipient-meta{min-width:0;flex:1}.avm-recipient-meta span{display:block;color:#64748b;font-size:11px;margin-bottom:4px}.avm-recipient-meta strong{display:block;color:#172033;font-size:13px;overflow-wrap:anywhere;margin-top:3px}
        .avm-live-label{font-size:10px;font-weight:800;letter-spacing:.4px;text-transform:uppercase;color:#16845b;background:#e6f6ec;padding:5px 7px;border-radius:6px;white-space:nowrap}
        .avm-whatsapp-preview{background:#eaf4eb;border:1px solid #d7e8d9;border-radius:11px;padding:10px}.avm-preview-top{display:flex;align-items:center;gap:8px;font-size:11px;font-weight:750;color:#315a42;margin-bottom:8px}
        .avm-preview-top {
  justify-content: space-between;
}

.avm-copy-icon {
  border: none;
  background: transparent;
  color: #315a42;
  cursor: pointer;
  padding: 5px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avm-copy-icon:hover {
  background: #dceee0;
}

.avm-copy-icon.copied {
  color: #16a34a;
  background: #dcfce7;
}
        .avm-whatsapp-bubble{background:#fff;padding:12px;border-radius:4px 11px 11px 11px;color:#334155;font-size:12px;line-height:1.55;box-shadow:0 2px 6px rgba(15,23,42,.035);overflow-wrap:anywhere}
        .avm-preview-link{display:block;color:#16845b;text-decoration:underline;font-weight:700;margin-top:8px;overflow-wrap:anywhere}.avm-preview-foot{font-size:10px;color:#7b8b7e;text-align:right;margin-top:8px}
        .avm-whatsapp-button{width:100%;background:#16845b;color:#fff;margin-top:14px}.avm-whatsapp-button:hover:not(:disabled){background:#116b49}.avm-send-note{font-size:11px;line-height:1.5;color:#64748b;margin:11px 0 0}
        .avm-status-head{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}.avm-pill{display:inline-block;padding:6px 10px;border-radius:999px;font-size:12px;font-weight:750;text-transform:capitalize}
        .avm-status-verified{background:#dcfce7;color:#166534}.avm-status-failed{background:#ffe4e6;color:#9f1239}.avm-status-pending{background:#fef3c7;color:#92400e}
        .avm-details{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-top:18px}.avm-detail{padding:14px;border:1px solid #e2e8f0;border-radius:11px;min-width:0;background:#fbfdff}
        .avm-detail span{display:block;font-size:12px;color:#64748b;margin-bottom:5px}.avm-detail strong{font-size:14px;overflow-wrap:anywhere}
        @media(max-width:760px){.avm-page{padding:10px}.avm-card{padding:14px}.avm-grid,.avm-details,.avm-share-grid{grid-template-columns:1fr}.avm-linkrow{flex-direction:column}.avm-share-panel{padding:17px}}
      `}</style>

      <header className="avm-header">
        <h1>Aadhaar Verification</h1>
        <p>Generate a secure verification link, review the recipient and message, then send it directly through WhatsApp.</p>
      </header>

      {message && (
        <div className={`avm-message avm-message-${messageType}`} role="status" aria-live="polite">
          {message}
        </div>
      )}

      <section className="avm-card">
        <h2>Create verification link</h2>
        <p className="avm-note" style={{ marginTop: 0, marginBottom: 20 }}>
          Only authorized administrators can create links or check verification status.
        </p>

        <form onSubmit={createLink}>
          <div className="avm-grid">
            <div className="avm-field">
              <label htmlFor="avm-user-id">User ID</label>
              <input
                id="avm-user-id"
                type="number"
                min="1"
                step="1"
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setStatusData(null);
                  setVerificationUrl("");
                  setRecipient(null);
                  notify("");
                }}
                placeholder="Enter user ID, e.g. 67323"
                required
              />
            </div>
            <div className="avm-field">
              <label htmlFor="avm-expiry">Link expiry</label>
              <select id="avm-expiry" value={expiresMinutes} onChange={(e) => setExpiresMinutes(e.target.value)}>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="180">3 hours</option>
                <option value="360">6 hours</option>
                <option value="720">12 hours</option>
                <option value="1440">24 hours</option>
                <option value="2520">42 hours</option>
                <option value="2880">48 hours</option>
              </select>
            </div>
          </div>

          <div className="avm-actions">
            <button className="avm-btn avm-primary" type="submit" disabled={!!loading}>
              {loading === "create" ? "Generating link…" : "＋ Generate verification link"}
            </button>
            <button className="avm-btn avm-secondary" type="button" onClick={checkStatus} disabled={!!loading}>
              {loading === "status" ? "Checking status…" : "Check verification status"}
            </button>
          </div>
        </form>

        {verificationUrl && (
          <div className="avm-share-grid">
            <section className="avm-share-panel" aria-labelledby="avm-link-panel-title">
              <div className="avm-panel-heading">
                <div className="avm-panel-icon" aria-hidden="true">↗</div>
                <div>
                  <h3 id="avm-link-panel-title">Verification link</h3>
                  <p>Generated for the selected user. Keep this link private.</p>
                </div>
              </div>
              <div className="avm-meta-row">
                <span>Recipient user ID</span><strong>{recipient?.userId ?? userId}</strong>
              </div>
              <div className="avm-meta-row">
                <span>Link validity</span><strong>{recipient?.expiresMinutes || Number(expiresMinutes)} minutes</strong>
              </div>
              <label className="avm-url-label" htmlFor="avm-generated-link">Private verification URL</label>
              <input className="avm-url-value" id="avm-generated-link" value={verificationUrl} readOnly onFocus={(e) => e.target.select()} />
              <div className="avm-actions">
                <button className="avm-btn avm-primary" type="button" onClick={copyLink} disabled={!!loading}>Copy link</button>
                <button className="avm-btn avm-secondary" type="button" onClick={() => window.open(verificationUrl, "_blank", "noopener,noreferrer")} disabled={!!loading}>Open link ↗</button>
              </div>
              <p className="avm-note">The link contains a private verification token. Share it only with the intended recipient.</p>
            </section>

            <section className="avm-share-panel avm-wa-panel" aria-labelledby="avm-wa-panel-title">
              <div className="avm-panel-heading">
                <div className="avm-panel-icon avm-panel-icon-green" aria-hidden="true">◉</div>
                <div>
                  <h3 id="avm-wa-panel-title">WhatsApp message preview</h3>
                  <p>Review the registered number before sending.</p>
                </div>
              </div>
              <div className="avm-recipient">
                <div className="avm-recipient-avatar" aria-hidden="true">
                  {(recipient?.name || "U").trim().charAt(0).toUpperCase()}
                </div>
                <div className="avm-recipient-meta">
                  <span>Sending to</span>
                  <strong>
                    {recipient?.name || `User ${recipient?.userId ?? userId}`} 
                    ({recipient?.phone || "Phone number unavailable"})
                  </strong>
                </div>
                <span className="avm-live-label">Recipient</span>
              </div>
                          
              <div className="avm-whatsapp-preview">
                <div className="avm-preview-top">
  <span>WhatsApp message preview</span>

  <button
  type="button"
  className={`avm-copy-icon ${messageCopied ? "copied" : ""}`}
  onClick={copyPreviewMessage}
  title={messageCopied ? "Copied!" : "Copy complete message"}
  aria-label={messageCopied ? "Message copied" : "Copy complete WhatsApp message"}
>
  {messageCopied ? (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12l4 4L19 6" />
    </svg>
  ) : (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="8" y="8" width="13" height="13" rx="2" />
      <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
    </svg>
  )}
</button>
</div>

                <div className="avm-whatsapp-bubble">
                  <div>
                    Hello {recipient?.name || "Customer"},
                  </div>

                  <div className="avm-preview-paragraph">
                    As part of the Snap4Me account verification process,
                    please complete your Aadhaar verification using the
                    secure link below:
                  </div>
                  
                  <a
                    className="avm-preview-link"
                    href={verificationUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {verificationUrl}
                  </a>

                  <div className="avm-preview-paragraph">
                    Please complete the verification to proceed with
                    your account verification process.
                  </div>
                 <br />
                  <div>
                  
                    Thank you,
                    <br />
                    Team Snap4Me
                    </div>         

                </div>

              </div>

              <button className="avm-btn avm-whatsapp-button" type="button" onClick={sendWhatsAppLink} disabled={!!loading || !verificationUrl || !recipient?.phone}>
                {loading === "whatsapp" ? "Sending WhatsApp…" : "Send WhatsApp link"}
              </button>
              <p className="avm-send-note">The actual outbound text is determined by your approved Twilio WhatsApp template. Make sure this preview wording matches that template.</p>
            </section>
          </div>
        )}
      </section>

      <section className="avm-card">
        <div className="avm-status-head">
          <div>
            <h2>Verification status</h2>
            <p className="avm-note" style={{ marginTop: 0 }}>Enter a user ID and select “Check verification status” to fetch the latest saved status.</p>
          </div>
          {statusData && <span className={`avm-pill ${statusClass}`}>{statusLabel}</span>}
        </div>

        {statusData ? (
          <>
            <div className="avm-details">
              <div className="avm-detail"><span>User ID</span><strong>{statusData.user_id ?? userId}</strong></div>
              <div className="avm-detail"><span>Status</span><strong>{statusLabel}</strong></div>
              <div className="avm-detail"><span>Attempts</span><strong>{statusData.attempts ?? 0}</strong></div>
              {statusData.aadhaar_number && <div className="avm-detail"><span>Aadhaar number</span><strong>{statusData.aadhaar_number}</strong></div>}
              <div className="avm-detail"><span>Provider request ID</span><strong>{statusData.provider_request_id || "Not available yet"}</strong></div>
              <div className="avm-detail"><span>Created at</span><strong>{statusData.created_at || "Not available"}</strong></div>
              <div className="avm-detail"><span>Verified at</span><strong>{statusData.verified_at || "Not verified yet"}</strong></div>
            </div>
            {statusData.status === "verified" && <p className="avm-message avm-message-success" style={{ marginTop: 18, marginBottom: 0 }}>Aadhaar verification is complete for this user.</p>}
            {statusData.status === "failed" && <p className="avm-message avm-message-error" style={{ marginTop: 18, marginBottom: 0 }}>Verification has reached the failed state. Generate a new verification link if appropriate.</p>}
          </>
        ) : (
          <p className="avm-note">No status loaded yet.</p>
        )}
      </section>
    </div>
  );
}
